import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faDownload, faTrash, faPlay, faMusic, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { formatDistanceToNow } from 'date-fns';
import { useUser } from '../../../../context/UserContext';
import { supabase } from '../../../../lib/supabase';
import { Client } from '../../../../context/ClientContext';
import { MediaFile, formatFileSize, getMediaUrl } from './mediaUtils';
import { KebabMenu } from '../../../ui';

interface MediaGridItemProps {
  file: MediaFile;
  client?: Client;
  onPreview: (file: MediaFile) => void;
  onMediaAdded: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

export default function MediaGridItem({
  file,
  client,
  onPreview,
  onMediaAdded,
  onError,
  onSuccess,
}: MediaGridItemProps) {
  const { user } = useUser();

  const getMediaPreview = (file: MediaFile) => {
    if (!file.mime_type) return null;

    const mediaUrl = getMediaUrl(file, client);

    if (file.mime_type.startsWith('image/')) {
      return (
        <img
          src={mediaUrl}
          alt={file.alt_text || file.object_key.split('/').pop()}
          style={{ width: '100%', height: '128px', objectFit: 'cover', display: 'block' }}
        />
      );
    }

    if (file.mime_type.startsWith('video/')) {
      return (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '128px',
            background: 'var(--ink-950)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FontAwesomeIcon icon={faPlay} style={{ color: '#fff', fontSize: '20px', zIndex: 1 }} />
          <video
            src={mediaUrl}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.5,
            }}
            muted
          />
        </div>
      );
    }

    if (file.mime_type.startsWith('audio/')) {
      return (
        <div
          style={{
            width: '100%',
            height: '128px',
            background: 'var(--surface-active)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FontAwesomeIcon icon={faMusic} style={{ color: 'var(--text-faint)', fontSize: '20px' }} />
        </div>
      );
    }

    if (file.mime_type === 'application/pdf') {
      return (
        <div
          style={{
            width: '100%',
            height: '128px',
            background: 'var(--surface-active)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FontAwesomeIcon icon={faFilePdf} style={{ color: 'var(--text-faint)', fontSize: '20px' }} />
        </div>
      );
    }

    return null;
  };

  const handleDownload = async (file: MediaFile) => {
    try {
      // Use direct URL from media domain or generate signed URL via Netlify function
      let downloadUrl: string;

      if (client?.media_domain) {
        // Use direct public URL
        downloadUrl = `https://${client.media_domain}/${file.object_key}`;
      } else {
        // Fallback: get signed URL via Netlify function
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No authentication session');

        const response = await fetch(
          `/.netlify/functions/r2-media-manager?clientId=${client?.id}&objectKey=${file.object_key}`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
            },
          }
        );

        if (!response.ok) throw new Error('Failed to get download URL');

        const result = await response.json();
        downloadUrl = result.signedUrl;
      }

      // Download the file
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Failed to download file');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.object_key.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      onError('Failed to download file');
    }
  };

  const handleDelete = async (file: MediaFile) => {
    const fileName = file.object_key.split('/').pop() || 'this file';
    if (!confirm(`Are you sure you want to delete "${fileName}"?`)) return;

    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authentication session');

      // Call Netlify function to delete
      const response = await fetch('/.netlify/functions/r2-media-manager', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          clientId: client?.id,
          mediaAssetId: file.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      onMediaAdded();
      onSuccess('Media file deleted successfully!');
    } catch (err) {
      console.error('Error deleting file:', err);
      onError(`Failed to delete file: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const canDelete = file.uploaded_by === user?.id || user?.role === 'admin' || user?.role === 'agency_owner';

  return (
    <div
      className="hover-lift"
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Media Preview */}
      <div style={{ cursor: 'pointer' }} onClick={() => onPreview(file)}>
        {getMediaPreview(file)}
      </div>

      {/* File Info */}
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              className="t-num"
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-body)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {file.object_key.split('/').pop()}
            </div>
            <div className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
              {formatFileSize(file.size_bytes)} ·{' '}
              {formatDistanceToNow(new Date(file.created_at), { addSuffix: true })}
            </div>
          </div>
          <KebabMenu
            label="File actions"
            actions={[
              { label: 'Preview', icon: faEye, onClick: () => onPreview(file) },
              { label: 'Download', icon: faDownload, onClick: () => handleDownload(file) },
              canDelete
                ? { label: 'Delete', icon: faTrash, danger: true, onClick: () => handleDelete(file) }
                : null,
            ]}
          />
        </div>
      </div>
    </div>
  );
}
